import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  ShippingOfferDuration,
  ShippingPolicy,
  ShippingPolicyProps,
  formatMinimumOrderStartDate,
  offerDurationToDays,
} from '@/lib/types/shipping-policy';
import { storeAtom } from '@/states/store.state';
import { Currency } from '@/types/currency';
import { Store } from '@/types/store';
import Button from '@appchoose/button';
import { Checkbox } from '@appchoose/checkbox';
import Input, { InputAdornment } from '@appchoose/input';
import { RadioGroup, RadioGroupItem } from '@appchoose/radio-group';
import { useAtomValue } from 'jotai';

interface FormErrors {
  minimumOrderAmount?: string;
}

function ShippingPolicyDisplay({
  policy,
  onPolicyChange,
  isLocked = false,
  open: controlledOpen,
  onOpenChange,
  hubspotStartDate,
}: ShippingPolicyProps & { onOpenChange?: (open: boolean) => void }) {
  const { t, i18n } = useTranslation();
  const store = useAtomValue(storeAtom);

  const [internalShowConfig, setInternalShowConfig] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const showConfig =
    controlledOpen !== undefined ? controlledOpen : internalShowConfig;
  const setShowConfig = (value: boolean) => {
    if (onOpenChange) {
      onOpenChange(value);
    } else {
      setInternalShowConfig(value);
    }
  };

  const [tempPolicy, setTempPolicy] = useState<ShippingPolicy>(policy);
  const [errors, setErrors] = useState<FormErrors>({});

  // Check if all shipping fees are 0
  const allShippingFeesZero = policy.allShippingFeesZero;
  const [previousShippingFeesZero, setPreviousShippingFeesZero] =
    useState(allShippingFeesZero);

  const bothOptionsEnabled =
    tempPolicy.minimumOrderEnabled && tempPolicy.timeLimitedEnabled;

  useEffect(() => {
    setTempPolicy(policy);
  }, [policy]);

  // Keep local form state aligned with "all shipping fees = 0".
  // Persistence is handled in useShippingPolicy to avoid modal-coupled API calls.
  useEffect(() => {
    if (isLocked) {
      setPreviousShippingFeesZero(allShippingFeesZero);
      return;
    }

    if (!showConfig) {
      return;
    }

    if (allShippingFeesZero && !previousShippingFeesZero) {
      setTempPolicy((current) => {
        const disabledPolicy: ShippingPolicy = {
          ...current,
          minimumOrderEnabled: false,
          timeLimitedEnabled: false,
          minimumOrderAmount: null,
          offerDuration: null,
        };
        return disabledPolicy;
      });
    }

    setPreviousShippingFeesZero(allShippingFeesZero);
  }, [allShippingFeesZero, previousShippingFeesZero, showConfig, isLocked]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (tempPolicy.minimumOrderEnabled) {
      if (
        tempPolicy.minimumOrderAmount === null ||
        tempPolicy.minimumOrderAmount === undefined
      ) {
        newErrors.minimumOrderAmount = t(
          'components.shipping_policy_configurator.error_required'
        );
      } else if (tempPolicy.minimumOrderAmount < 1) {
        newErrors.minimumOrderAmount = t(
          'components.shipping_policy_configurator.error_minimum_amount',
          {
            currency: store === Store.FR ? Currency.EUR : Currency.USD,
          }
        );
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onPolicyChange(tempPolicy);
      setShowConfig(false);
    }
  };

  const handleMinimumOrderChange = (value: number | null) => {
    const newValue = isNaN(value as number) ? null : value;

    setTempPolicy({
      ...tempPolicy,
      minimumOrderAmount: newValue,
    });

    if (newValue !== null && newValue < 1) {
      setErrors({
        ...errors,
        minimumOrderAmount: t(
          'components.shipping_policy_configurator.error_minimum_amount',
          {
            currency: store === Store.FR ? Currency.EUR : Currency.USD,
          }
        ),
      });
    } else {
      setErrors({
        ...errors,
        minimumOrderAmount: undefined,
      });
    }
  };

  const handleDurationChange = (duration: ShippingOfferDuration) => {
    setTempPolicy({
      ...tempPolicy,
      offerDuration: duration,
    });
  };

  // Ensure offerDuration is always a valid value when timeLimitedEnabled is true
  const currentDurationValue = tempPolicy.timeLimitedEnabled
    ? (tempPolicy.offerDuration ?? '24h')
    : '24h';

  const toggleTimeLimited = () => {
    setTempPolicy((current) => {
      const newEnabled = !current.timeLimitedEnabled;
      return {
        ...current,
        timeLimitedEnabled: newEnabled,
        offerDuration: newEnabled
          ? (current.offerDuration ?? '24h')
          : current.offerDuration,
      };
    });
  };

  const toggleMinimumOrder = () => {
    setTempPolicy((current) => {
      const newEnabled = !current.minimumOrderEnabled;
      return {
        ...current,
        minimumOrderEnabled: newEnabled,
        minimumOrderAmount: newEnabled
          ? (current.minimumOrderAmount ?? 1)
          : current.minimumOrderAmount,
      };
    });
    setErrors({});
  };

  const currency = store === Store.FR ? Currency.EUR : Currency.USD;
  const currencyIcon = store === Store.FR ? '€' : '$';

  const getEndDateForDuration = (
    duration: ShippingOfferDuration
  ): string | null => {
    const startDate = hubspotStartDate
      ? new Date(hubspotStartDate)
      : new Date();
    const days = offerDurationToDays(duration);
    if (!days) return null;

    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + days);

    const formattedDate = endDate.toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'short',
    });

    return formattedDate;
  };

  const getStartDate = (): string | null => {
    return formatMinimumOrderStartDate(
      hubspotStartDate,
      tempPolicy.timeLimitedEnabled,
      tempPolicy.offerDuration,
      i18n.language
    );
  };

  const minimumOrderStartDate = tempPolicy.timeLimitedEnabled
    ? getStartDate()
    : null;

  return (
    <Dialog open={showConfig} onOpenChange={setShowConfig}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('components.other.free_shipping')}</DialogTitle>
          <DialogDescription>
            {t(
              'components.import_flow.products_option_selection.choose_option'
            )}
            :
          </DialogDescription>
        </DialogHeader>

        <div className="mb-6 space-y-4">
          {/* Show explanatory message when shipping fees are 0 */}
          {allShippingFeesZero && (
            <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-800">
              <div className="flex items-start gap-2">
                <span className="text-blue-600">ℹ️</span>
                {t(
                  'components.shipping_policy_configurator.disabled_when_zero_fees',
                  {
                    currency,
                  }
                )}
              </div>
            </div>
          )}

          <div className="space-y-3 rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id="time-limited"
                checked={tempPolicy.timeLimitedEnabled}
                disabled={isLocked || allShippingFeesZero}
                onCheckedChange={toggleTimeLimited}
                className="h-[16px] w-[16px]"
              />
              <Label
                htmlFor="time-limited"
                className={
                  isLocked || allShippingFeesZero
                    ? 'cursor-not-allowed text-gray-400'
                    : 'cursor-pointer'
                }
              >
                {t(
                  'components.shipping_policy_configurator.for_limited_duration'
                )}
              </Label>
            </div>

            {tempPolicy.timeLimitedEnabled && (
              <div className="space-y-3 pl-7">
                <p className="text-sm text-gray-600">
                  {t('components.shipping_policy_configurator.choose_duration')}
                </p>
                <RadioGroup
                  value={currentDurationValue}
                  onValueChange={(value) =>
                    handleDurationChange(value as ShippingOfferDuration)
                  }
                  className="flex flex-col gap-3"
                >
                  {(['24h', '48h', '72h'] as ShippingOfferDuration[]).map(
                    (duration) => {
                      const endDate = getEndDateForDuration(duration);
                      return (
                        <div key={duration} className="flex items-center gap-3">
                          <div className="flex items-center space-x-3">
                            <RadioGroupItem
                              value={duration}
                              id={`duration-${duration}`}
                              disabled={isLocked || allShippingFeesZero}
                              className="h-[16px] w-[16px]"
                            />
                            <Label
                              htmlFor={`duration-${duration}`}
                              className="cursor-pointer text-sm font-medium text-gray-700 peer-disabled:cursor-default peer-disabled:text-gray-300 peer-data-[state=checked]:text-gray-900 peer-data-[state=checked]:peer-disabled:text-gray-300"
                            >
                              {duration}
                            </Label>
                          </div>
                          {endDate && (
                            <p className="text-xs text-muted-foreground">
                              {t(
                                'components.shipping_policy_configurator.ends_on_at',
                                { date: endDate }
                              )}
                            </p>
                          )}
                        </div>
                      );
                    }
                  )}
                </RadioGroup>
              </div>
            )}
          </div>

          {/* Minimum order amount option */}
          <div className="space-y-3 rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id="minimum-order"
                checked={tempPolicy.minimumOrderEnabled}
                disabled={isLocked || allShippingFeesZero}
                onCheckedChange={toggleMinimumOrder}
                className="h-[16px] w-[16px]"
              />
              <Label
                htmlFor="minimum-order"
                className={
                  isLocked || allShippingFeesZero
                    ? 'cursor-not-allowed text-gray-400'
                    : 'cursor-pointer'
                }
              >
                {t('components.other.min_purchase_amount')}
              </Label>
            </div>

            {tempPolicy.minimumOrderEnabled && (
              <div className="space-y-2 pl-7">
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={tempPolicy.minimumOrderAmount ?? ''}
                  disabled={isLocked || allShippingFeesZero}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newValue =
                      e.target.value === '' ? null : Number(e.target.value);
                    handleMinimumOrderChange(newValue);
                  }}
                  onBlur={() => {
                    if (tempPolicy.minimumOrderAmount === null) {
                      setErrors({
                        ...errors,
                        minimumOrderAmount: t(
                          'components.shipping_policy_configurator.error_required'
                        ),
                      });
                    }
                  }}
                  placeholder={t(
                    'components.shipping_policy_configurator.amount_placeholder'
                  )}
                  className={`bg-white dark:bg-white ${
                    errors.minimumOrderAmount
                      ? 'border-red-500 focus:ring-red-500'
                      : ''
                  }`}
                  startAdornment={
                    <InputAdornment position="start">
                      {currencyIcon}
                    </InputAdornment>
                  }
                />
                {errors.minimumOrderAmount && (
                  <div className="mt-1 text-xs text-red-500">
                    {errors.minimumOrderAmount}
                  </div>
                )}
                {/* Only show start date if time-limited is also enabled (Phase 2 starts after Phase 1) */}
                {minimumOrderStartDate && (
                  <p className="text-xs text-muted-foreground">
                    {t('components.shipping_policy_configurator.starts_on_at', {
                      date: minimumOrderStartDate,
                    })}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Info block when both options are enabled */}
          {bothOptionsEnabled && (
            <div className="rounded-md bg-blue-50 p-3 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-blue-600">ℹ️</span>
                <div className="flex-1 space-y-1">
                  <p className="font-medium text-blue-900">
                    {t('components.shipping_policy_configurator.info_title')}
                  </p>
                  <p className="text-blue-800">
                    {t(
                      'components.shipping_policy_configurator.info_description'
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <DialogFooter>
          <Button
            type="button"
            appearance="outline"
            onClick={() => {
              setShowConfig(false);
            }}
          >
            {t('general.cancel')}
          </Button>
          <Button
            type="button"
            appearance="primary"
            onClick={
              allShippingFeesZero ? () => setShowConfig(false) : handleSave
            }
            disabled={isLocked}
          >
            {allShippingFeesZero ? t('general.close') : t('general.validate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ShippingPolicyDisplay;
